import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import actionRotateIconUrl from '@/assets/action-menu/reset-password.svg'
import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { SearchInput } from '@/components/forms/search-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { getModelProviderMeta, listRegisteredModels } from '@/features/admin/model-provider/api'
import { ProviderLogo } from '@/features/admin/model-provider/provider-logo'
import { useAdminListControls } from '@/features/admin/shared/use-admin-list-controls'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { formatDateTime } from '@/features/admin/shared/format'
import { ApiError } from '@/lib/api/http-client'

import {
  deleteModelDistributionKey,
  listModelDistributionKeys,
  rotateModelDistributionKey,
  type ModelDistributionKey,
  type ModelDistributionKeyQuotaRule,
  type ModelDistributionKeyStatus,
} from './api'
import { AdminModelKeysFormDrawer } from './form-drawer'

const pageSizeOptions = [10, 20, 50]
const statusValues = ['active', 'disabled'] as const
const defaultColumnIds = [
  'name',
  'key',
  'models',
  'protocol',
  'quota',
  'status',
  'created-by',
  'created-at',
  'actions',
]

interface DrawerState {
  keyId?: string
  mode: 'create' | 'edit'
  open: boolean
}

interface RevealedSecret {
  keyId: string
  name: string
  value: string
}

type ModelDistributionKeyStatusFilter = ModelDistributionKeyStatus | ''

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
    </svg>
  )
}

function KeyNameCell({ value }: { value: string }) {
  return (
    <span className="block max-w-[140px] truncate font-semibold text-[#1a1c19]" title={value}>
      {value}
    </span>
  )
}

function SecretCell({ value }: { value: string }) {
  return (
    <span
      className="inline-flex max-w-[160px] rounded-md bg-[var(--color-hover-muted)] px-2 py-1 font-mono text-xs text-[var(--color-text)]"
      title={value}
    >
      <span className="truncate">{value}</span>
    </span>
  )
}

function CreatorCell({ value }: { value?: string | null }) {
  const trimmed = value?.trim()
  const normalized = trimmed && trimmed.length > 0 ? trimmed : '-'

  return (
    <span
      className="block max-w-[116px] truncate text-sm text-[var(--color-text)]"
      title={normalized}
    >
      {normalized}
    </span>
  )
}

function providerLabel(model: { provider: string; provider_label?: string }) {
  return model.provider_label ?? model.provider.toUpperCase()
}

function modelLabel(model: { model_name: string }) {
  return model.model_name
}

function formatQuotaSummary(
  rules: ModelDistributionKeyQuotaRule[] | undefined,
  emptyLabel: string,
) {
  if (!rules?.length) {
    return emptyLabel
  }

  const enabledCount = rules.filter((rule) => rule.enabled !== false).length

  return `${String(enabledCount)}/${String(rules.length)}`
}

function quotaVariant(rules: ModelDistributionKeyQuotaRule[] | undefined) {
  if (!rules?.length) {
    return 'neutral' as const
  }

  return rules.some((rule) => rule.enabled !== false) ? ('info' as const) : ('warning' as const)
}

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.message
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message
  }

  return fallback
}

export function AdminModelKeysPage() {
  const { t } = useTranslation('adminModelKeys')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const {
    page,
    pageSize,
    search,
    searchInput,
    setPage,
    setPageSize,
    setSearchInput,
    setVisibleColumnIds,
    visibleColumnIds,
    visibleColumnIdSet,
  } = useAdminListControls({ defaultColumnIds })
  const [status, setStatus] = useState<ModelDistributionKeyStatusFilter>('active')
  const [drawerState, setDrawerState] = useState<DrawerState>({ mode: 'create', open: false })
  const [deleteTarget, setDeleteTarget] = useState<ModelDistributionKey | null>(null)
  const [rotateTarget, setRotateTarget] = useState<ModelDistributionKey | null>(null)
  const [revealedSecret, setRevealedSecret] = useState<RevealedSecret | null>(null)

  const metaQuery = useQuery({
    queryFn: getModelProviderMeta,
    queryKey: ['admin-model-provider-meta'],
  })

  const modelsQuery = useQuery({
    queryFn: () =>
      listRegisteredModels({
        page: 1,
        pageSize: 500,
        status: 'active',
      }),
    queryKey: [
      'admin-registered-models',
      { page: 1, pageSize: 500, status: 'active', surface: 'model-keys' },
    ],
  })

  const keysQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listModelDistributionKeys({
        page,
        pageSize,
        search,
        status,
      }),
    queryKey: ['admin-model-distribution-keys', { page, pageSize, search, status }],
  })

  const protocolLabelByKey = useMemo(
    () =>
      new Map((metaQuery.data?.protocols ?? []).map((protocol) => [protocol.key, protocol.label])),
    [metaQuery.data?.protocols],
  )
  const statusOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allStatuses'), value: '' },
      ...statusValues.map((item) => ({
        label: t(`status.${item}`),
        value: item,
      })),
    ],
    [t],
  )

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteModelDistributionKey(keyId),
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('deleteDialog.failed')),
        message: t('deleteDialog.failed'),
        scope: 'global',
        tone: 'error',
      })
    },
    onSuccess: () => {
      showFeedback({
        message: t('deleteDialog.success'),
        scope: 'global',
        tone: 'success',
      })
      setDeleteTarget(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-model-distribution-keys'] })
    },
  })

  const rotateMutation = useMutation({
    mutationFn: (key: ModelDistributionKey) => rotateModelDistributionKey(key.id),
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('rotateDialog.failed')),
        message: t('rotateDialog.failed'),
        scope: 'global',
        tone: 'error',
      })
    },
    onSuccess: (savedKey, originalKey) => {
      showFeedback({
        message: t('rotateDialog.success'),
        scope: 'global',
        tone: 'success',
      })
      if (savedKey.plain_key) {
        setRevealedSecret({
          keyId: savedKey.id,
          name: savedKey.name || originalKey.name,
          value: savedKey.plain_key,
        })
      }
      setRotateTarget(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-model-distribution-keys'] })
    },
  })

  const columns = useMemo<DataTableColumn<ModelDistributionKey>[]>(
    () => [
      {
        header: t('table.name'),
        hidden: !visibleColumnIdSet.has('name'),
        id: 'name',
        pin: 'left',
        render: (key) => <KeyNameCell value={key.name} />,
        visibilityLabel: t('table.name'),
        width: 176,
      },
      {
        header: t('table.key'),
        hidden: !visibleColumnIdSet.has('key'),
        id: 'key',
        render: (key) => <SecretCell value={key.masked_key ?? key.key_prefix} />,
        visibilityLabel: t('table.key'),
        width: 170,
      },
      {
        header: t('table.allowedModels'),
        hidden: !visibleColumnIdSet.has('models'),
        id: 'models',
        render: (key) => (
          <div className="grid max-w-full justify-items-start gap-1.5 py-1 whitespace-normal">
            {key.allowed_models.map((model) => (
              <span
                className="inline-flex min-h-7 w-fit max-w-full items-center gap-1.5 rounded-full bg-[var(--color-hover-muted)] px-2 py-1 text-xs leading-5 text-[var(--color-text)]"
                key={model.id}
                title={`${providerLabel(model)} / ${modelLabel(model)}`}
              >
                <ProviderLogo
                  className="size-4"
                  label={providerLabel(model)}
                  provider={model.provider}
                  variant="compact"
                />
                <span className="min-w-0 [overflow-wrap:anywhere]">{modelLabel(model)}</span>
              </span>
            ))}
          </div>
        ),
        visibilityLabel: t('table.allowedModels'),
        width: 280,
      },
      {
        header: t('table.outputProtocol'),
        hidden: !visibleColumnIdSet.has('protocol'),
        id: 'protocol',
        render: (key) => (
          <Badge variant="neutral">
            {protocolLabelByKey.get(key.output_protocol) ?? key.output_protocol}
          </Badge>
        ),
        visibilityLabel: t('table.outputProtocol'),
        width: 220,
      },
      {
        header: t('table.quota'),
        hidden: !visibleColumnIdSet.has('quota'),
        id: 'quota',
        render: (key) => (
          <Badge variant={quotaVariant(key.quota_rules)}>
            {formatQuotaSummary(key.quota_rules, t('table.noQuota'))}
          </Badge>
        ),
        visibilityLabel: t('table.quota'),
        width: 130,
      },
      {
        header: t('table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (key) => (
          <Badge variant={key.status === 'active' ? 'success' : 'danger'}>
            {t(`status.${key.status}`)}
          </Badge>
        ),
        visibilityLabel: t('table.status'),
        width: 90,
      },
      {
        header: t('table.createdBy'),
        hidden: !visibleColumnIdSet.has('created-by'),
        id: 'created-by',
        render: (key) => <CreatorCell value={key.created_by_name ?? key.created_by} />,
        visibilityLabel: t('table.createdBy'),
        width: 140,
      },
      {
        header: t('table.createdAt'),
        hidden: !visibleColumnIdSet.has('created-at'),
        id: 'created-at',
        render: (key) => formatDateTime(key.created_at),
        visibilityLabel: t('table.createdAt'),
        width: 165,
      },
      {
        align: 'center',
        header: t('table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (key) => (
          <ActionMenu
            ariaLabel={t('actions.open', { name: key.name })}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'edit',
                label: t('actions.edit'),
                onSelect: () => {
                  setDrawerState({ keyId: key.id, mode: 'edit', open: true })
                },
              },
              {
                disabled: rotateMutation.isPending,
                iconSrc: actionRotateIconUrl,
                key: 'rotate',
                label: t('actions.rotate'),
                onSelect: () => setRotateTarget(key),
              },
              {
                iconSrc: actionDeleteIconUrl,
                key: 'delete',
                label: t('actions.delete'),
                onSelect: () => setDeleteTarget(key),
                variant: 'danger',
              },
            ]}
          />
        ),
        visibilityLabel: t('table.actions'),
        width: 70,
      },
    ],
    [protocolLabelByKey, rotateMutation.isPending, t, visibleColumnIdSet],
  )

  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  const listData = keysQuery.data
  const keys = listData?.items ?? []
  const total = listData?.total ?? 0
  const showError = keysQuery.isError && !keysQuery.data
  const errorDescription =
    keysQuery.error instanceof ApiError
      ? t('errorDescriptionWithDetail', { detail: keysQuery.error.message })
      : t('errorDescription')

  async function copyRevealedValue(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      showFeedback({
        message: t('reveal.copySuccess'),
        scope: 'global',
        tone: 'success',
      })
    } catch {
      showFeedback({
        message: t('reveal.copyFailed'),
        scope: 'global',
        tone: 'error',
      })
    }
  }

  return (
    <AdminShellPage activePath="/admin/model-keys">
      <section
        aria-label={t('workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-model-keys-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button
                disabled={!metaQuery.data || modelsQuery.isError}
                onClick={() => setDrawerState({ mode: 'create', open: true })}
                size="sm"
              >
                <PlusIcon />
                {t('actions.add')}
              </Button>
            </>
          }
          description={t('description')}
          title={t('title')}
        />
        <FilterBar>
          <SearchInput
            aria-label={t('filters.searchLabel')}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
          />
          <Select
            aria-label={t('filters.statusLabel')}
            onValueChange={(value) => {
              setStatus(value as ModelDistributionKeyStatusFilter)
              setPage(1)
            }}
            options={statusOptions}
            value={status}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {showError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => void keysQuery.refetch()}
                  size="sm"
                  variant="secondary"
                >
                  {t('retry')}
                </Button>
              }
              description={errorDescription}
              title={t('errorTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('empty')}
                getRowKey={(key) => key.id}
                isLoading={keysQuery.isLoading}
                rows={keys}
              />
              <Pagination
                disabled={keysQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                total={total}
              />
            </>
          )}
        </div>
        <AdminModelKeysFormDrawer
          keyId={drawerState.keyId}
          meta={metaQuery.data}
          mode={drawerState.mode}
          models={modelsQuery.data?.items ?? []}
          onOpenChange={(open) => {
            setDrawerState((current) => ({
              ...current,
              open,
            }))
          }}
          onSaved={(savedKey) => {
            if (savedKey?.plain_key) {
              setRevealedSecret({
                keyId: savedKey.id,
                name: savedKey.name,
                value: savedKey.plain_key,
              })
            }
          }}
          open={drawerState.open}
        />
        <DeleteKeyDialog
          isSubmitting={deleteMutation.isPending}
          modelKey={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            if (deleteTarget) {
              deleteMutation.mutate(deleteTarget.id)
            }
          }}
          open={Boolean(deleteTarget)}
        />
        <RotateKeyDialog
          isSubmitting={rotateMutation.isPending}
          modelKey={rotateTarget}
          onClose={() => setRotateTarget(null)}
          onConfirm={() => {
            if (rotateTarget) {
              rotateMutation.mutate(rotateTarget)
            }
          }}
          open={Boolean(rotateTarget)}
        />
        {revealedSecret ? (
          <RevealedSecretDialog
            key={revealedSecret.keyId}
            onClose={() => setRevealedSecret(null)}
            onCopy={(value) => void copyRevealedValue(value)}
            secret={revealedSecret}
          />
        ) : null}
      </section>
    </AdminShellPage>
  )
}

function RevealedSecretDialog({
  onClose,
  onCopy,
  secret,
}: {
  onClose: () => void
  onCopy: (value: string) => void
  secret: RevealedSecret
}) {
  const { t } = useTranslation('adminModelKeys')

  return (
    <Modal
      closeLabel={t('reveal.close')}
      footer={
        <Button onClick={onClose} size="sm">
          {t('reveal.confirm')}
        </Button>
      }
      onClose={onClose}
      open
      subtitle={t('reveal.description')}
      title={t('reveal.title', { name: secret.name })}
    >
      <div className="grid gap-4">
        <CopyableReadonlyField
          copyLabel={t('reveal.copySecret')}
          label={t('reveal.secret')}
          monospace
          onCopy={() => onCopy(secret.value)}
          showLabel
          value={secret.value}
        />
      </div>
    </Modal>
  )
}

function DeleteKeyDialog({
  isSubmitting,
  modelKey,
  onClose,
  onConfirm,
  open,
}: {
  isSubmitting: boolean
  modelKey?: ModelDistributionKey | null
  onClose: () => void
  onConfirm: () => void
  open: boolean
}) {
  const { t } = useTranslation('adminModelKeys')

  return (
    <Modal
      closeLabel={t('deleteDialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('deleteDialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm} size="sm" variant="danger">
            {t('deleteDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      subtitle={t('deleteDialog.subtitle')}
      title={t('deleteDialog.title')}
    >
      <p className="text-sm leading-6 text-[var(--color-text)]">
        {t('deleteDialog.message', { name: modelKey?.name ?? '' })}
      </p>
    </Modal>
  )
}

function RotateKeyDialog({
  isSubmitting,
  modelKey,
  onClose,
  onConfirm,
  open,
}: {
  isSubmitting: boolean
  modelKey?: ModelDistributionKey | null
  onClose: () => void
  onConfirm: () => void
  open: boolean
}) {
  const { t } = useTranslation('adminModelKeys')

  return (
    <Modal
      closeLabel={t('rotateDialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('rotateDialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm} size="sm">
            {t('rotateDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      subtitle={t('rotateDialog.subtitle')}
      title={t('rotateDialog.title')}
    >
      <p className="text-sm leading-6 text-[var(--color-text)]">
        {t('rotateDialog.message', { name: modelKey?.name ?? '' })}
      </p>
    </Modal>
  )
}
